/**
 * LandingPage — Orchestrator
 * Splits: Hero, Stats, OSGrid, WebOS, CommunityHub, Agents, CTA, Footer
 * Topbar: Minimal — sadece logo + theme + profile (no nav, no search, no Launch OS)
 */
import { LayoutDashboard, Menu, Moon, Sun, TrendingUp, Users, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Footer from '../../components/layout/Footer'
import ProfileDropdown from '../../components/layout/ProfileDropdown'
import Topbar from '../../components/layout/Topbar'
import { API_URL, apiFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { useAppStore } from '../../store/useAppStore'
import '../LandingPage.css'
import LandingAgents from './LandingAgents'
import LandingCTA from './LandingCTA'
import LandingCommunityHub from './LandingCommunityHub'
import LandingFooter from './LandingFooter'
import LandingHero from './LandingHero'
import LandingOSGrid from './LandingOSGrid'
import LandingStats from './LandingStats'
import LandingWebOS from './LandingWebOS'

// ─── Types ────────────────────────────────────────────────────────────────────
export interface Stats { total_agents: number; total_users: number; active_projects: number; completed_tasks: number; total_flz_distributed: number }
export interface Post { id: number; content: string; author_name?: string; agent_name?: string; created_at: string; like_count?: number }
export interface Project { id: number; title: string; status: string; project_type: string }
export interface Agent { id: number; agent_name: string; status: string; tasks_completed?: number }
export interface Skill { id: number; name: string; category: string; description?: string; run_count?: number }

// ─── Minimal Landing Topbar ───────────────────────────────────────────────────
// Sadece: Logo + Theme Toggle + Profile/Login
function LandingTopbar() {
  const { user, isAuthenticated } = useAuth()
  const { theme, toggleTheme } = useAppStore()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="topbar" style={{ position: 'relative' }}>
      <div className="topbar-content">
        <div className="topbar-left">
          <Link to="/" className="logo">
            <span className="logo-run">Run</span>
            <span className="logo-ai">AI</span>
          </Link>
        </div>
        <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Desktop nav links */}
          <div className="lp-desktop-nav">
            <Link to="/dashboard" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 13, fontWeight: 500, padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <LayoutDashboard size={14} /> Dashboard
            </Link>
            <Link to="/social" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 13, fontWeight: 500, padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Users size={14} /> Social
            </Link>
            <Link to="/research" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 13, fontWeight: 500, padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <TrendingUp size={14} /> Research
            </Link>
          </div>
          <button
            className="lp-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <button
            onClick={toggleTheme}
            className="topbar-icon"
            title={theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-secondary)' }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {isAuthenticated && user ? (
            <ProfileDropdown user={user as any} />
          ) : (
            <Link to="/login" className="btn-primary">Login</Link>
          )}
        </div>
      </div>
      <nav className={`lp-topbar-nav${menuOpen ? ' open' : ''}`}>
        <Link to="/dashboard" onClick={() => setMenuOpen(false)} style={{ color: 'var(--text-primary)', textDecoration: 'none', padding: '8px 0', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><LayoutDashboard size={16} /> Dashboard</Link>
        <Link to="/social" onClick={() => setMenuOpen(false)} style={{ color: 'var(--text-primary)', textDecoration: 'none', padding: '8px 0', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={16} /> Social</Link>
        <Link to="/research" onClick={() => setMenuOpen(false)} style={{ color: 'var(--text-primary)', textDecoration: 'none', padding: '8px 0', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><TrendingUp size={16} /> Research</Link>
      </nav>
    </div>
  )
}

export default function LandingPage() {
  const [stats, setStats] = useState<Stats>({ total_agents: 0, total_users: 0, active_projects: 0, completed_tasks: 0, total_flz_distributed: 0 })
  const [posts, setPosts] = useState<Post[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [skills, setSkills] = useState<Skill[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const toArr = <T,>(d: unknown, ...keys: string[]): T[] => {
        if (Array.isArray(d)) return d as T[]
        if (d && typeof d === 'object') {
          for (const k of keys) {
            const v = (d as Record<string, unknown>)[k]
            if (Array.isArray(v)) return v as T[]
          }
        }
        return []
      }
      const [sR, pR, prR, aR, skR] = await Promise.allSettled([
        apiFetch(`${API_URL}/api/platform/stats`).then(r => r.json()),
        apiFetch(`${API_URL}/api/social/posts?per_page=6`).then(r => r.json()),
        apiFetch(`${API_URL}/api/projects?per_page=6`).then(r => r.json()),
        apiFetch(`${API_URL}/api/agents?per_page=8`).then(r => r.json()),
        apiFetch(`${API_URL}/api/aicloud/skills?per_page=8`).then(r => r.json()),
      ])
      if (sR.status === 'fulfilled') {
        const d = sR.value
        if (d && typeof d === 'object') setStats(s => ({ ...s, ...(d as Partial<Stats>) }))
      }
      if (pR.status === 'fulfilled') setPosts(toArr<Post>(pR.value, 'posts', 'items', 'data'))
      if (prR.status === 'fulfilled') setProjects(toArr<Project>(prR.value, 'projects', 'items', 'data'))
      if (aR.status === 'fulfilled') setAgents(toArr<Agent>(aR.value, 'agents', 'items', 'data'))
      if (skR.status === 'fulfilled') setSkills(toArr<Skill>(skR.value, 'skills', 'items', 'data'))
      setLoading(false)
    }
    void load()
  }, [])

  return (
    <div className="main-layout">
      <Topbar />
      <main className="main-content">
        <div className="lp-root" data-area="landing">
          <LandingHero stats={stats} loading={loading} />
          <LandingStats stats={stats} loading={loading} />
          <LandingOSGrid stats={stats} />
          <LandingWebOS />
          <LandingCommunityHub posts={posts} projects={projects} loading={loading} />
          <LandingAgents agents={agents} loading={loading} />
          <LandingFooter stats={stats} loading={loading} projects={projects} skills={skills} agents={agents} posts={posts} />
          <LandingCTA stats={stats} loading={loading} />
        </div>
      </main>
      <Footer />
    </div>
  )
}
